import * as React from "react"

/**
 * Keyboard behaviour shared by every master-detail list (one item selected, its details beside it): the team
 * panel, the members list preview, the help dialogs, the member journey.
 *
 * - The arrow keys move the selection (selection follows focus, so the detail updates at once); Home / End jump
 *   to the ends, Page Up / Page Down by a page, and typing letters jumps to the next item whose label starts
 *   with them.
 * - Enter / Space run the item's primary action when the list has one (`onActivate`); otherwise selection has
 *   already happened and they do nothing more.
 * - The list is one tab stop: only the selected item is in the tab order (roving tabindex), so Tab moves on
 *   into the detail.
 * - Keys stay quiet while typing in a field, with a modifier held, or while a dialog or menu is open above the
 *   list (a list inside that dialog keeps working).
 *
 * The caller owns the selection (local state or the URL); a URL selection should write with `replace`
 * (see `useSearchParamSelection`) so moving through the list never piles up history entries.
 */

export enum SelectableListOrientation {
  Vertical = "vertical",
  Horizontal = "horizontal",
}

/** How far Page Up / Page Down jump. */
export const SELECTABLE_LIST_PAGE_SIZE = 10

/** Typed letters belong to one search until the user pauses this long. */
export const SELECTABLE_LIST_TYPEAHEAD_MS = 500

/** Open layers that take the keyboard: dialogs, alert dialogs and menus (Radix marks them `data-state="open"`). */
const OPEN_LAYER_SELECTOR = [
  '[role="dialog"][data-state="open"]',
  '[role="alertdialog"][data-state="open"]',
  '[role="menu"][data-state="open"]',
].join(", ")

/** True when an open dialog or menu sits above `element` (one that contains it does not count). */
export function isLayerOpenAbove(element: Element | null): boolean {
  if (typeof document === "undefined") return false
  return Array.from(document.querySelectorAll(OPEN_LAYER_SELECTOR)).some((layer) => !element || !layer.contains(element))
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT"
}

/** Case- and accent-blind, so "e" finds "Émilie". */
function normalizeLabel(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
}

type SelectableListKeyboardOptions<T> = {
  items: readonly T[]
  getId: (item: T) => string
  /** The text typeahead matches against (usually the visible name). */
  getLabel: (item: T) => string
  selectedId: string | null | undefined
  onSelect: (item: T) => void
  /** The item's primary action (open the full record), run on Enter / Space. Leave it out when there is none. */
  onActivate?: (item: T) => void
  orientation?: SelectableListOrientation
  pageSize?: number
}

type SelectableListProps = {
  role: "listbox"
  "aria-orientation": SelectableListOrientation
  onKeyDown: React.KeyboardEventHandler<HTMLElement>
  ref: React.RefCallback<HTMLElement>
}

export type SelectableListItemProps = {
  role: "option"
  "aria-selected": boolean
  tabIndex: 0 | -1
  "data-selected"?: "true"
  ref: React.RefCallback<HTMLElement>
  onClick: React.MouseEventHandler<HTMLElement>
}

export function useSelectableListKeyboard<T>({
  items,
  getId,
  getLabel,
  selectedId,
  onSelect,
  onActivate,
  orientation = SelectableListOrientation.Vertical,
  pageSize = SELECTABLE_LIST_PAGE_SIZE,
}: SelectableListKeyboardOptions<T>) {
  const listRef = React.useRef<HTMLElement | null>(null)
  const itemRefs = React.useRef(new Map<string, HTMLElement>())
  const refCallbacks = React.useRef(new Map<string, React.RefCallback<HTMLElement>>())
  const typeahead = React.useRef({ buffer: "", at: 0 })

  // A selection that is not in the list (stale link, filtered out) falls back to the first item for focus.
  const selectedIndex = items.findIndex((item) => getId(item) === selectedId)
  const focusIndex = selectedIndex >= 0 ? selectedIndex : 0

  const reveal = React.useCallback((id: string) => {
    const node = itemRefs.current.get(id)
    if (!node) return
    node.focus({ preventScroll: true })
    node.scrollIntoView?.({ block: "nearest", inline: "nearest" })
  }, [])

  const moveTo = React.useCallback(
    (index: number) => {
      const item = items[Math.max(0, Math.min(items.length - 1, index))]
      if (!item) return
      const id = getId(item)
      if (id !== selectedId) onSelect(item)
      reveal(id)
    },
    [getId, items, onSelect, reveal, selectedId],
  )

  /** Puts focus on the selected item (or the first): a search field above the list uses it for ArrowDown. */
  const focusSelected = React.useCallback(() => moveTo(focusIndex), [focusIndex, moveTo])

  const findByTypeahead = (buffer: string): number => {
    const needle = normalizeLabel(buffer)
    // A fresh single letter starts after the current item so repeating it cycles; a longer word refines in place.
    const start = buffer.length === 1 ? focusIndex + 1 : focusIndex
    for (let offset = 0; offset < items.length; offset += 1) {
      const index = (start + offset) % items.length
      if (normalizeLabel(getLabel(items[index] as T)).startsWith(needle)) return index
    }
    return -1
  }

  const onKeyDown: React.KeyboardEventHandler<HTMLElement> = (event) => {
    if (event.defaultPrevented || items.length === 0) return
    if (event.altKey || event.ctrlKey || event.metaKey) return
    if (isTypingTarget(event.target)) return
    if (isLayerOpenAbove(listRef.current)) return

    const vertical = orientation === SelectableListOrientation.Vertical
    const previousKey = vertical ? "ArrowUp" : "ArrowLeft"
    const nextKey = vertical ? "ArrowDown" : "ArrowRight"
    const handled = (index: number) => {
      event.preventDefault()
      moveTo(index)
    }

    switch (event.key) {
      case nextKey:
        return handled(focusIndex + 1)
      case previousKey:
        return handled(focusIndex - 1)
      case "Home":
        return handled(0)
      case "End":
        return handled(items.length - 1)
      case "PageDown":
        return handled(focusIndex + pageSize)
      case "PageUp":
        return handled(focusIndex - pageSize)
      case "Enter": {
        const item = items[focusIndex]
        if (!onActivate || !item) return
        event.preventDefault()
        onActivate(item)
        return
      }
    }

    const now = Date.now()
    const typing = typeahead.current.buffer.length > 0 && now - typeahead.current.at < SELECTABLE_LIST_TYPEAHEAD_MS
    if (event.key === " " && !typing) {
      const item = items[focusIndex]
      if (!onActivate || !item) return
      event.preventDefault()
      onActivate(item)
      return
    }
    if (event.key.length !== 1) return

    const buffer = (typing ? typeahead.current.buffer : "") + event.key
    typeahead.current = { buffer, at: now }
    event.preventDefault()
    const match = findByTypeahead(buffer)
    if (match >= 0) moveTo(match)
  }

  const listRefCallback = React.useCallback((node: HTMLElement | null) => {
    listRef.current = node
  }, [])

  const getListProps = (): SelectableListProps => ({
    role: "listbox",
    "aria-orientation": orientation,
    onKeyDown,
    ref: listRefCallback,
  })

  const itemRef = (id: string): React.RefCallback<HTMLElement> => {
    let callback = refCallbacks.current.get(id)
    if (!callback) {
      callback = (node: HTMLElement | null) => {
        if (node) itemRefs.current.set(id, node)
        else itemRefs.current.delete(id)
      }
      refCallbacks.current.set(id, callback)
    }
    return callback
  }

  const getItemProps = (item: T, index: number): SelectableListItemProps => {
    const id = getId(item)
    const selected = selectedIndex >= 0 ? index === selectedIndex : false
    return {
      role: "option",
      "aria-selected": selected,
      tabIndex: index === focusIndex ? 0 : -1,
      "data-selected": selected ? "true" : undefined,
      ref: itemRef(id),
      onClick: (event) => {
        // Safari does not focus a clicked button; the arrow keys need focus in the list to work after a click.
        event.currentTarget.focus({ preventScroll: true })
        if (id !== selectedId) onSelect(item)
      },
    }
  }

  return { getListProps, getItemProps, focusSelected }
}
